import { ApiError } from "../../api";
import { countOf, formatEpoch, ownValue, safeMessage, tidy } from "../shared/safe";

// Pure parsing and wording for the per-client facts screens (approved facts, targets, unresolved information, citation conflicts, GBP
// records, history). Everything the server sends is untrusted: strings may hold markup, JSON columns may be absent or the wrong shape, URLs
// may be `javascript:`. Nothing here touches the DOM. Shapes: docs/build/handoffs/worker-a-routes.md section 3, captured in
// tests/contract/fixtures/routes-dto.json.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const nonEmpty = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

export function httpUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

// ---- field catalogue -------------------------------------------------------------------------------------------------------------

export type FieldKind = "text" | "textarea" | "number" | "select" | "json";
export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Hint under the input. */
  help?: string;
  options?: readonly string[];
}

export const STATUS_OPTIONS = ["onboarding", "active", "paused", "archived"] as const;

/** The approved fact fields this page edits. `mode` and the AI budget are policy, not facts: the server refuses them here, so they are not offered. */
export const FACT_SPECS: readonly FieldSpec[] = [
  { key: "business_name", label: "Business name", kind: "text" },
  { key: "legal_name", label: "Legal name", kind: "text" },
  { key: "status", label: "Status", kind: "select", options: STATUS_OPTIONS },
  { key: "primary_category", label: "Primary category", kind: "text" },
  { key: "niche", label: "Niche", kind: "text" },
  { key: "address", label: "Address", kind: "text" },
  { key: "city", label: "City", kind: "text" },
  { key: "state", label: "State", kind: "text" },
  { key: "zip", label: "Zip", kind: "text" },
  { key: "phone", label: "Phone", kind: "text", help: "Stored in one canonical format." },
  { key: "email", label: "Email", kind: "text" },
  { key: "owner_name", label: "Owner name", kind: "text" },
  { key: "owner_role", label: "Owner role", kind: "text" },
  { key: "founding_year", label: "Founding year", kind: "number" },
  { key: "money_site_url", label: "Money site URL", kind: "text", help: "The main website. Edit target pages on the Targets card." },
  { key: "claim_block", label: "Claim block", kind: "textarea", help: "Byte-identical on every publication." },
  { key: "services_json", label: "Services (JSON)", kind: "json", help: 'A list: [{"name": "...", "price": "...", "description": "..."}]' },
  { key: "service_area_json", label: "Service area (JSON)", kind: "json", help: 'A list of place names: ["Exampleville", "Sampletown"]' },
  { key: "attributes_json", label: "Attributes (JSON)", kind: "json", help: 'A list: [{"label": "...", "value": "..."}]. Written into generated content, so only confirmed facts belong here.' },
  { key: "hours_json", label: "Hours (JSON)", kind: "json", help: 'For example {"mon": {"open": "09:00", "close": "17:00"}, "sun": null}' },
  { key: "podcast_owner_email", label: "Podcast owner email", kind: "text" },
];
/** Shown but not edited here. */
export const READONLY_JSON_SPECS: readonly FieldSpec[] = [
  { key: "money_pages_json", label: "Target pages", kind: "json" },
  { key: "target_rules_json", label: "Link rules", kind: "json" },
  { key: "voice_profile_json", label: "Voice profile", kind: "json" },
  { key: "template_selection_json", label: "Template selection", kind: "json" },
];
export const SPEC_BY_KEY: Record<string, FieldSpec> = Object.fromEntries([...FACT_SPECS, ...READONLY_JSON_SPECS, { key: "mode", label: "Mode", kind: "text" }, { key: "monthly_ai_budget_cents", label: "AI budget (cents)", kind: "number" }, { key: "gbp_location_id", label: "GBP location id", kind: "text" }].map((s) => [s.key, s as FieldSpec]));
export const fieldLabel = (key: string): string => ownValue(SPEC_BY_KEY, key)?.label ?? tidy(key, 60);

/** Fields an unresolved item may propose (a fact column, or "other" which is a note that can never be promoted). */
export const UNRESOLVED_FIELDS: readonly string[] = [...FACT_SPECS.filter((s) => s.key !== "status").map((s) => s.key), "other"];
export const SOURCE_KINDS = ["owner", "client", "website", "gbp", "citation", "ai_inference", "import", "other"] as const;
export const SOURCE_KIND_LABELS: Record<string, string> = { owner: "The owner", client: "The client", website: "A website", gbp: "A GBP listing", citation: "A citation or directory", ai_inference: "AI inference", import: "An import", other: "Other" };
export const CONFLICT_FIELDS = ["name", "address", "phone", "email", "website", "hours", "category", "other"] as const;
export const CONFLICT_STATUSES = ["open", "resolved", "accepted_variation", "dismissed"] as const;
export const CONFLICT_STATUS_LABELS: Record<string, string> = { open: "Open", resolved: "Resolved", accepted_variation: "Accepted variation", dismissed: "Dismissed" };
export const GBP_STATUSES = ["unverified", "owner_confirmed", "archived"] as const;
export const GBP_STATUS_LABELS: Record<string, string> = { unverified: "Unverified", owner_confirmed: "Owner confirmed", archived: "Archived" };
export const PAGE_KINDS = ["home", "service", "location", "about", "contact", "blog", "other"] as const;

// ---- value display -----------------------------------------------------------------------------------------------------------------

/** A stored or parsed fact value as short plain text. JSON values are compact JSON. */
export function showValue(v: unknown, max = 400): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return tidy(v, max);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return tidy(JSON.stringify(v), max);
  } catch {
    return "(unreadable value)";
  }
}
export const prettyJson = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return "";
  }
};

// ---- facts -------------------------------------------------------------------------------------------------------------------------

export interface PublishingStatement {
  /** null when the server did not say. The page shows exactly what it said. */
  directApi: boolean | null;
  statement: string | null;
}
export function readPublishing(v: unknown): PublishingStatement {
  const o = isObject(v) ? v : {};
  return { directApi: typeof o.directApi === "boolean" ? o.directApi : null, statement: nonEmpty(o.statement) ? safeMessage(o.statement, 600) : null };
}

export interface FactsView {
  clientId: string;
  slug: string;
  revision: number;
  approved: Record<string, unknown>;
  parseIssues: string[];
  unresolvedOpen: number;
  unresolvedNote: string | null;
  conflictsOpen: number;
  gbpProfiles: number;
  gbpLocationId: string | null;
  publishing: PublishingStatement;
}

export function parseFacts(body: unknown): FactsView | null {
  const f = isObject(body) && isObject(body.facts) ? body.facts : null;
  if (!f || !isObject(f.approved)) return null;
  const clientId = nonEmpty(f.clientId);
  const revision = int(f.revision);
  if (clientId === null || revision === null) return null;
  const unresolved = isObject(f.unresolved) ? f.unresolved : {};
  const gbp = isObject(f.gbp) ? f.gbp : {};
  return {
    clientId,
    slug: tidy(f.slug, 120),
    revision,
    approved: f.approved,
    parseIssues: (Array.isArray(f.parseIssues) ? f.parseIssues : []).map((x) => (typeof x === "string" ? safeMessage(x, 300) : isObject(x) ? safeMessage(x.message ?? JSON.stringify(x), 300) : "")).filter(Boolean),
    unresolvedOpen: countOf(unresolved.open),
    unresolvedNote: nonEmpty(unresolved.note) ? safeMessage(unresolved.note, 400) : null,
    conflictsOpen: countOf(isObject(f.citationConflicts) ? f.citationConflicts.open : 0),
    gbpProfiles: countOf(gbp.profiles),
    gbpLocationId: nonEmpty(gbp.locationId),
    publishing: readPublishing(gbp.publishing),
  };
}

// ---- history -----------------------------------------------------------------------------------------------------------------------

export interface HistoryEntry {
  id: string;
  changedBy: string;
  /** The local boundary labels its callers: "local-dashboard" (this dashboard) or "local-mcp" (an AI client). */
  actorKind: "dashboard" | "mcp" | "other";
  when: { text: string; iso: string } | null;
  source: string;
  reason: string | null;
  identityVerified: boolean | null;
  /** "created": the facts the client was created with (not a change; it does not count in the revision). "changed": an edit. Null on servers that do not say. */
  kind: "created" | "changed" | null;
  changes: { field: string; before: string; after: string }[];
}
export interface PageInfo {
  total: number;
  limit: number;
  offset: number;
  returned: number;
  hasMore: boolean;
}
export function readPageInfo(v: unknown): PageInfo | null {
  if (!isObject(v)) return null;
  const total = int(v.total);
  const limit = int(v.limit);
  const offset = int(v.offset);
  const returned = int(v.returned);
  return total === null || limit === null || limit < 1 || offset === null || returned === null || typeof v.hasMore !== "boolean" ? null : { total, limit, offset, returned, hasMore: v.hasMore };
}

export const actorKind = (by: string): HistoryEntry["actorKind"] => (by === "local-dashboard" ? "dashboard" : by === "local-mcp" ? "mcp" : "other");
export const ACTOR_LABELS: Record<HistoryEntry["actorKind"], string> = { dashboard: "the dashboard (you)", mcp: "an AI client through MCP", other: "" };

export function parseHistory(body: unknown): { entries: HistoryEntry[]; page: PageInfo | null } | null {
  if (!isObject(body) || !Array.isArray(body.history)) return null;
  const entries = body.history.filter(isObject).map((x): HistoryEntry => {
    const by = nonEmpty(x.changedBy) ? tidy(x.changedBy, 120) : "unknown";
    const diff = isObject(x.diff) ? x.diff : {};
    return {
      id: nonEmpty(x.id) ? tidy(x.id, 60) : "",
      changedBy: by,
      actorKind: actorKind(by),
      when: formatEpoch(x.createdAt),
      source: nonEmpty(x.source) ? tidy(x.source, 40) : "unknown",
      reason: nonEmpty(x.reason) ? tidy(x.reason, 300) : null,
      identityVerified: typeof x.identityVerified === "boolean" ? x.identityVerified : null,
      kind: x.kind === "created" || x.kind === "changed" ? x.kind : null,
      changes: Object.entries(diff).map(([field, ch]) => ({ field: tidy(field, 60), before: isObject(ch) ? showValue(ch.before, 200) : "—", after: isObject(ch) ? showValue(ch.after, 200) : "—" })),
    };
  });
  return { entries, page: readPageInfo(body.page) };
}

// ---- targets -----------------------------------------------------------------------------------------------------------------------

export interface TargetPage {
  label: string;
  url: string;
  kind: string | null;
  service: string | null;
  location: string | null;
  aliases: string[];
  comparisonUrl: string | null;
}
export interface TargetsView {
  revision: number;
  moneySiteUrl: string | null;
  moneySiteComparisonUrl: string | null;
  pages: TargetPage[];
  parseIssue: string | null;
  gbpLinks: { profileId: string; label: string; status: string; mapsUrl: string | null; publicUrl: string | null; reviewUrl: string | null }[];
  proposals: UnresolvedItem[];
  publishing: PublishingStatement;
}
export function parseTargets(body: unknown): TargetsView | null {
  const t = isObject(body) && isObject(body.targets) ? body.targets : null;
  if (!t) return null;
  const revision = int(t.revision);
  if (revision === null) return null;
  const gbp = isObject(t.gbp) ? t.gbp : {};
  return {
    revision,
    moneySiteUrl: nonEmpty(t.moneySiteUrl),
    moneySiteComparisonUrl: nonEmpty(t.moneySiteComparisonUrl),
    pages: (Array.isArray(t.pages) ? t.pages : []).filter(isObject).map((p) => ({
      label: tidy(p.label, 200),
      url: str(p.url) ?? "",
      kind: nonEmpty(p.kind) ? tidy(p.kind, 30) : null,
      service: nonEmpty(p.service) ? tidy(p.service, 200) : null,
      location: nonEmpty(p.location) ? tidy(p.location, 200) : null,
      aliases: (Array.isArray(p.aliases) ? p.aliases : []).filter((a): a is string => typeof a === "string"),
      comparisonUrl: nonEmpty(p.comparisonUrl),
    })),
    parseIssue: nonEmpty(t.parseIssue) ? safeMessage(t.parseIssue, 300) : null,
    gbpLinks: (Array.isArray(gbp.links) ? gbp.links : []).filter(isObject).map((l) => ({ profileId: tidy(l.profileId, 60), label: tidy(l.label, 200), status: tidy(l.status, 30), mapsUrl: nonEmpty(l.mapsUrl), publicUrl: nonEmpty(l.publicUrl), reviewUrl: nonEmpty(l.reviewUrl) })),
    proposals: (Array.isArray(t.unresolvedProposals) ? t.unresolvedProposals : []).map(readUnresolved).filter((x): x is UnresolvedItem => x !== null),
    publishing: readPublishing(gbp.publishing),
  };
}

// ---- unresolved information --------------------------------------------------------------------------------------------------------

export interface UnresolvedItem {
  id: string;
  field: string;
  label: string | null;
  proposedValue: string;
  sourceKind: string;
  sourceRef: string | null;
  note: string | null;
  status: "unresolved" | "promoted" | "rejected";
  /** From the server: always false. The page never shows an unresolved item as an approved fact, whatever this says. */
  matchesApproved: boolean | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: { text: string; iso: string } | null;
  createdBy: string;
  createdAt: { text: string; iso: string } | null;
}
export function readUnresolved(x: unknown): UnresolvedItem | null {
  if (!isObject(x) || !nonEmpty(x.id)) return null;
  const status = x.status === "promoted" || x.status === "rejected" ? x.status : "unresolved";
  return {
    id: x.id as string,
    field: nonEmpty(x.field) ? tidy(x.field, 60) : "other",
    label: nonEmpty(x.label) ? tidy(x.label, 200) : null,
    proposedValue: typeof x.proposedValue === "string" ? x.proposedValue : showValue(x.proposedValue, 4000),
    sourceKind: nonEmpty(x.sourceKind) ? tidy(x.sourceKind, 30) : "other",
    sourceRef: nonEmpty(x.sourceRef) ? tidy(x.sourceRef, 300) : null,
    note: nonEmpty(x.note) ? tidy(x.note, 600) : null,
    status,
    matchesApproved: typeof x.matchesApproved === "boolean" ? x.matchesApproved : null,
    resolutionNote: nonEmpty(x.resolutionNote) ? tidy(x.resolutionNote, 600) : null,
    resolvedBy: nonEmpty(x.resolvedBy) ? tidy(x.resolvedBy, 120) : null,
    resolvedAt: formatEpoch(x.resolvedAt),
    createdBy: nonEmpty(x.createdBy) ? tidy(x.createdBy, 120) : "unknown",
    createdAt: formatEpoch(x.createdAt),
  };
}
export function parseUnresolvedList(body: unknown): UnresolvedItem[] | null {
  return isObject(body) && Array.isArray(body.unresolved) ? body.unresolved.map(readUnresolved).filter((x): x is UnresolvedItem => x !== null) : null;
}

/** What promoting an item would do, from what the page knows now. Shown BEFORE the confirmation so the person sees exactly what becomes approved. */
export interface PromotionPreview {
  promotable: boolean;
  /** Why not, in words. */
  reason: string | null;
  field: string;
  fieldLabel: string;
  before: string;
  after: string;
  /** The item's value already equals the approved value (the server says so, or the displayed values are identical). */
  unchanged: boolean;
}
export function previewPromotion(item: UnresolvedItem, approved: Record<string, unknown>): PromotionPreview {
  const fieldLabelText = item.field === "other" ? (item.label ?? "Other") : fieldLabel(item.field);
  if (item.field === "other") return { promotable: false, reason: 'This is a note ("other"), not a fact field, so it cannot become an approved fact. Reject it, or record the fact on the approved facts card with its own field.', field: item.field, fieldLabel: fieldLabelText, before: "—", after: tidy(item.proposedValue, 600), unchanged: false };
  if (!(item.field in approved) && item.field !== "status") return { promotable: false, reason: "The server did not report an approved value for this field, so the page cannot show what would change.", field: item.field, fieldLabel: fieldLabelText, before: "—", after: tidy(item.proposedValue, 600), unchanged: false };
  const before = showValue(approved[item.field], 600);
  const after = tidy(item.proposedValue, 600);
  const same = item.matchesApproved === true || before === after;
  return { promotable: true, reason: null, field: item.field, fieldLabel: fieldLabelText, before, after, unchanged: same };
}

// ---- citation conflicts ------------------------------------------------------------------------------------------------------------

export interface ConflictItem {
  id: string;
  field: string;
  approvedWhenRecorded: string;
  currentApproved: string;
  changedSinceRecorded: boolean;
  conflictingValue: string;
  sourceName: string;
  sourceUrl: string | null;
  note: string | null;
  status: string;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: { text: string; iso: string } | null;
  createdBy: string;
  createdAt: { text: string; iso: string } | null;
}
export function readConflict(x: unknown): ConflictItem | null {
  if (!isObject(x) || !nonEmpty(x.id)) return null;
  return {
    id: x.id as string,
    field: nonEmpty(x.field) ? tidy(x.field, 30) : "other",
    approvedWhenRecorded: showValue(x.approvedValueWhenRecorded, 300),
    currentApproved: showValue(x.currentApprovedValue, 300),
    changedSinceRecorded: x.approvedValueChangedSinceRecorded === true,
    conflictingValue: showValue(x.conflictingValue, 400),
    sourceName: nonEmpty(x.sourceName) ? tidy(x.sourceName, 200) : "(unnamed source)",
    sourceUrl: nonEmpty(x.sourceUrl),
    note: nonEmpty(x.note) ? tidy(x.note, 600) : null,
    status: nonEmpty(x.status) ? tidy(x.status, 30) : "open",
    resolutionNote: nonEmpty(x.resolutionNote) ? tidy(x.resolutionNote, 600) : null,
    resolvedBy: nonEmpty(x.resolvedBy) ? tidy(x.resolvedBy, 120) : null,
    resolvedAt: formatEpoch(x.resolvedAt),
    createdBy: nonEmpty(x.createdBy) ? tidy(x.createdBy, 120) : "unknown",
    createdAt: formatEpoch(x.createdAt),
  };
}
export function parseConflicts(body: unknown): ConflictItem[] | null {
  return isObject(body) && Array.isArray(body.conflicts) ? body.conflicts.map(readConflict).filter((x): x is ConflictItem => x !== null) : null;
}

// ---- GBP ---------------------------------------------------------------------------------------------------------------------------

export interface GbpProfile {
  id: string;
  label: string;
  listingName: string | null;
  locationResource: string | null;
  placeId: string | null;
  mapsUrl: string | null;
  publicUrl: string | null;
  reviewUrl: string | null;
  primaryCategory: string | null;
  address: string | null;
  phone: string | null;
  note: string | null;
  status: string;
  /** looksLikeGoogleMapsLink per URL: true / false / null (no URL). */
  urlFlags: { mapsUrl: boolean | null; publicUrl: boolean | null; reviewUrl: boolean | null };
  updatedBy: string | null;
  updatedAt: { text: string; iso: string } | null;
}
export function readGbpProfile(x: unknown): GbpProfile | null {
  if (!isObject(x) || !nonEmpty(x.id)) return null;
  const flags = isObject(x.urlFlags) ? x.urlFlags : {};
  const flag = (v: unknown) => (typeof v === "boolean" ? v : null);
  const t = (v: unknown, n = 300) => (nonEmpty(v) ? tidy(v, n) : null);
  return {
    id: x.id as string,
    label: t(x.label, 200) ?? "(unlabelled profile)",
    listingName: t(x.listingName),
    locationResource: t(x.locationResource),
    placeId: t(x.placeId),
    mapsUrl: nonEmpty(x.mapsUrl),
    publicUrl: nonEmpty(x.publicUrl),
    reviewUrl: nonEmpty(x.reviewUrl),
    primaryCategory: t(x.primaryCategory),
    address: t(x.address),
    phone: t(x.phone, 60),
    note: t(x.note, 600),
    status: t(x.status, 30) ?? "unverified",
    urlFlags: { mapsUrl: flag(flags.mapsUrl), publicUrl: flag(flags.publicUrl), reviewUrl: flag(flags.reviewUrl) },
    updatedBy: t(x.updatedBy, 120),
    updatedAt: formatEpoch(x.updatedAt),
  };
}
export function parseGbp(body: unknown): { profiles: GbpProfile[]; locationId: string | null; publishing: PublishingStatement } | null {
  const g = isObject(body) && isObject(body.gbp) ? body.gbp : null;
  if (!g || !Array.isArray(g.profiles)) return null;
  return { profiles: g.profiles.map(readGbpProfile).filter((p): p is GbpProfile => p !== null), locationId: nonEmpty(g.locationId), publishing: readPublishing(g.publishing) };
}

// ---- failures ----------------------------------------------------------------------------------------------------------------------

export interface Problem {
  /** stale: the revision guard refused. validation: values refused (issues). gone: already resolved / not found. network: not known whether anything was saved. other: any other refusal. */
  kind: "stale" | "validation" | "resolved" | "notfound" | "unauthorized" | "network" | "server" | "other";
  message: string;
  issues: { field: string; message: string }[];
  /** The server's current revision, when it said (409 STALE_REVISION). */
  revision: number | null;
}

export function describeProblem(e: unknown, what: string): Problem {
  if (!(e instanceof ApiError)) return { kind: "network", message: `${what}: couldn't reach the server, so it is not known whether anything was saved. Reload to see what the server has.`, issues: [], revision: null };
  const body = e.body ?? {};
  const issues = (Array.isArray(body.issues) ? body.issues : []).filter(isObject).map((i) => ({ field: tidy(i.field, 60), message: safeMessage(i.message, 300) }));
  const revision = int(body.revision);
  const message = safeMessage(e.message, 400);
  if (e.code === "STALE_REVISION") return { kind: "stale", message: "Conflict: this client's facts changed after you loaded them (someone or something else saved first). Nothing was saved. Reload to see the current values.", issues: [], revision };
  if (e.code === "ALREADY_RESOLVED") return { kind: "resolved", message: `${message} Reload to see its current state.`, issues, revision };
  if (e.code === "VALIDATION_FAILED" || e.status === 422 || e.status === 400) return { kind: "validation", message: `${what}: ${message}`, issues, revision };
  if (e.status === 404) return { kind: "notfound", message: `${what}: not found (it may have been removed). Reload.`, issues, revision };
  if (e.status === 401 || e.status === 403) return { kind: "unauthorized", message: `${what}: not allowed (${e.status}). Nothing was saved.`, issues, revision };
  if (e.status >= 500) return { kind: "server", message: `${what}: the server failed (HTTP ${e.status}), so it is not known whether anything was saved. Reload to see what the server has.`, issues, revision };
  return { kind: "other", message: `${what}: ${message}`, issues, revision };
}

// ---- editing helpers ---------------------------------------------------------------------------------------------------------------

/** The text an input starts with for a fact value. */
export function inputText(spec: FieldSpec, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (spec.kind === "json") return prettyJson(value);
  return typeof value === "string" ? value : String(value);
}

/** Converts what a person typed into the value to send, or an error. Blank clears an optional field (null). */
export function parseInput(spec: FieldSpec, raw: string): { ok: true; value: unknown } | { ok: false; message: string } {
  const text = spec.kind === "json" || spec.kind === "textarea" ? raw : raw.trim();
  if (text.trim() === "") return { ok: true, value: null };
  if (spec.kind === "number") {
    const n = Number(text);
    return Number.isInteger(n) ? { ok: true, value: n } : { ok: false, message: "Enter a whole number." };
  }
  if (spec.kind === "json") {
    try {
      return { ok: true, value: JSON.parse(text) };
    } catch {
      return { ok: false, message: "Not valid JSON. Check the brackets, quotes and commas." };
    }
  }
  return { ok: true, value: text };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The fields whose typed value differs from the approved value. `errors` names inputs that cannot be sent. */
export function draftChanges(drafts: Record<string, string>, approved: Record<string, unknown>): { changes: Record<string, unknown>; errors: Record<string, string> } {
  const changes: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const spec of FACT_SPECS) {
    if (!(spec.key in drafts)) continue;
    const parsed = parseInput(spec, drafts[spec.key]);
    if (!parsed.ok) {
      errors[spec.key] = parsed.message;
      continue;
    }
    if (!same(parsed.value, approved[spec.key])) changes[spec.key] = parsed.value;
  }
  return { changes, errors };
}
